import React, { useMemo } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { FilterValue, FilterConfig } from "../types";
import { SortableFilterBadge } from "./SortableFilterBadge";

export interface FilterBadgesContainerProps {
  activeFilters: FilterValue[];
  activeFilterType: FilterValue['type'] | null;
  onActiveFilterTypeChange: (type: FilterValue['type'] | null) => void;
  onFiltersChange: (filters: FilterValue[]) => void;
  onRemoveFilter: (type: FilterValue['type']) => void;
  getFilterLabel: (filter: FilterValue) => string;
  renderFilterOptions: (filter: FilterValue) => React.ReactNode;
  getFilterConfig: (type: FilterValue['type']) => FilterConfig | undefined;
}

/**
 * Container component for filter badges with drag-and-drop reordering.
 * Wraps filter badges in a DnD context for reordering.
 */
export function FilterBadgesContainer({
  activeFilters,
  activeFilterType,
  onActiveFilterTypeChange,
  onFiltersChange,
  onRemoveFilter,
  getFilterLabel,
  renderFilterOptions,
  getFilterConfig,
}: FilterBadgesContainerProps) {
  // Drag-and-drop sensors for filter reordering
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Create unified list of all draggable filter items (sections/fields are now per-table, not global)
  const allDraggableItems = useMemo(() => {
    // Only include regular filters, not sections/fields
    return activeFilters.filter(f => f.type !== 'sections' && f.type !== 'fields');
  }, [activeFilters]);

  // Handle filter drag end - reorder filters
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = allDraggableItems.findIndex(f => f.type === active.id);
      const newIndex = allDraggableItems.findIndex(f => f.type === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedItems = arrayMove(allDraggableItems, oldIndex, newIndex);
        onFiltersChange(reorderedItems);
      }
    }
  };

  if (allDraggableItems.length === 0) {
    return null;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={allDraggableItems.map(f => f.type)}
        strategy={horizontalListSortingStrategy}
      >
        <div className="flex items-center gap-2">
          {allDraggableItems.map((filter) => (
            <SortableFilterBadge
              key={filter.type}
              filter={filter}
              isActive={activeFilterType === filter.type}
              onToggle={() => onActiveFilterTypeChange(activeFilterType === filter.type ? null : filter.type)}
              onRemove={() => onRemoveFilter(filter.type)}
              getLabel={() => getFilterLabel(filter)}
              renderOptions={() => renderFilterOptions(filter)}
              getConfig={() => getFilterConfig(filter.type)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export default FilterBadgesContainer;

